import React, { useRef, useState, useEffect } from "react";
import { EditOutlined, ArrowBackIos } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import {
  useGetUserDetails,
  useUpdateUser,
  useUpdateUserImage,
  useGetOrganizationDetails,
  useAdminResetUserPassword,
} from "../apiCalls/userApiCalls";
import Loader from "../components/Loader";
import { useSelector } from "react-redux";

export default function EditUser() {
  const nameInputElement = useRef();
  const codeInputElement = useRef();
  const emailInputElement = useRef();
  const roleInputElement = useRef();
  const hourlyRateInputElement = useRef();
  const overtimeRateInputElement = useRef();
  const organizationInputElement = useRef();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const { currentUser } = useSelector((state) => state.userSlice) || null;
  const token = currentUser.token;
  const { userId } = useParams();

  const { isLoading: isUserLoading, data: userDetails } = useGetUserDetails(
    userId,
    token
  );
  const organizationId = userDetails?.data.data.organization;

  // Always call the hook, but control the enabled state
  const { isLoading: isOrganizationLoading, data: organizationDetails } =
    useGetOrganizationDetails(organizationId, token, {
      enabled: !!organizationId, // Only enable the query if organizationId exists
    });

  const {
    mutate: updateUserMutate,
    isLoading: isUpdateUserLoading,
    isError: isUpdateUserError,
    error: updateUserError,
  } = useUpdateUser(userId);
  const {
    mutate: updateUserImageMutate,
    isLoading: isUpdateUserImageLoading,
    isError: isUpdateUserImageError,
    error: updateUserImageError,
  } = useUpdateUserImage();

  const {
    mutate: resetPasswordMutate,
    isLoading: isResetPasswordLoading,
  } = useAdminResetUserPassword();

  const handleFileChange = (e) => {
    const imageData = {
      token: token,
      userId: userId,
      image: e.target.files[0],
    };
    updateUserImageMutate(imageData);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const data = {
      userId: userId,
      token: token,
      name: nameInputElement.current?.value,
      employeeCode: codeInputElement.current?.value,
      email: emailInputElement.current?.value,
      role: roleInputElement.current?.value,
      hourlyRate: hourlyRateInputElement.current?.value,
      overtimeRate: overtimeRateInputElement.current?.value,
      organization: organizationId,
    };

    updateUserMutate(data);
  };

  const handleResetPassword = () => {
    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters long");
      return;
    }
    
    setPasswordError("");
    resetPasswordMutate({
      userId,
      newPassword,
      token
    });
    setIsModalOpen(false);
    setNewPassword("");
    setConfirmPassword("");
  };

  const allRoles = ["foreman", "worker", "sales"];
  const fallbackImage = "/images/avatar.jpg";

  return (
    <>
      {isUserLoading || isOrganizationLoading ? (
        <Loader />
      ) : (
        <>
          <div className="flex w-full items-center justify-center">
            <div className="mx-6 md:mx-32 lg:mx-44 xl:mx-64 mt-32 w-full lg:w-2/5">
              <div className="fixed lg:left-20 xl:left-32 mt-2 hidden lg:flex lg:items-center lg:justify-center items-center justify-center w-10 h-10 rounded-full bg-white shadow btn-shadow hover:shadow-s, hover:bg-stone-50 transition-colors">
                <button
                  onClick={() => window.history.back()}
                  className="w-full h-full rounded-full flex items-center justify-center"
                >
                  <ArrowBackIos
                    style={{ fontSize: 24, paddingLeft: 6, color: "gray" }}
                  />
                </button>
              </div>
              <div className="pt-10 flex flex-col mb-5 items-center">
                <form className="flex items-end">
                  <div
                    className="w-28 h-28 border border-gray-200  rounded-full"
                    style={{
                      backgroundImage: `url("${userDetails.data.data.imgUrl}"), url("${fallbackImage}")`,
                      backgroundPosition: "center",
                      backgroundSize: "cover",
                      backgroundRepeat: "no-repeat",
                    }}
                  ></div>
                  <div className="w-7 h-7 -ml-8 rounded-full bg-blue-400 text-white flex justify-center">
                    <label htmlFor="file">
                      <EditOutlined style={{ fontSize: "16" }} />
                    </label>
                  </div>
                  <input
                    type="file"
                    id="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />
                </form>

                {(userDetails.data.data.role === "foreman" ||
                      userDetails.data.data.role === "worker") && (
                      <button
                        type="button"
                        onClick={() => setIsModalOpen(true)}
                        className="px-2 py-1 mt-6 text-white text-sm rounded bg-[#59c3c2] hover:bg-teal-600"
                      >
                        Reset Password
                      </button>
                    )}
              </div>

              <form onSubmit={handleSubmit} className="pb-8">
                <div className="space-y-3">
                  <div className="space-y-2">
                    <label className="text-base text-gray-500" htmlFor="">
                      Name
                    </label>
                    <div className="rounded-xl py-4 px-5 flex gap-2 border border-black">
                      <input
                        type="text"
                        className="outline-none w-full text-base font-medium text-black placeholder-black"
                        name="name"
                        defaultValue={userDetails.data.data.name}
                        ref={nameInputElement}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-base text-gray-500" htmlFor="">
                      Employee ID
                    </label>
                    <div className="rounded-xl py-4 px-5 flex gap-2 border border-black">
                      <input
                        type="text"
                        className="outline-none w-full text-base font-medium text-black placeholder-black"
                        name="employeeCode"
                        defaultValue={userDetails.data.data.employeeCode}
                        ref={codeInputElement}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-base text-gray-500" htmlFor="">
                      Email Address
                    </label>
                    <div className="rounded-xl py-4 px-5 flex gap-2 border border-black">
                      <input
                        type="email"
                        className="outline-none w-full text-base font-medium text-black placeholder-black"
                        name="email"
                        defaultValue={userDetails.data.data.email}
                        ref={emailInputElement}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-base text-gray-500" htmlFor="">
                      Type
                    </label>
                    <div className="rounded-xl py-4 px-5 flex gap-2 border border-black">
                      <select
                        name="role"
                        className="w-full outline-none capitalize"
                        ref={roleInputElement}
                      >
                        <option value={userDetails.data.data.role}>
                          {userDetails.data.data.role}
                        </option>
                        {allRoles.map((role, index) => (
                          <React.Fragment key={index}>
                            {userDetails.data.data.role !== role && (
                              <option value={role} key={role}>
                                {role}
                              </option>
                            )}
                          </React.Fragment>
                        ))}
                      </select>
                    </div>
                  </div>
                  {userDetails.data.data.role !== "admin" && (
                    <>
                      <div className="space-y-2">
                        <label className="text-base text-gray-500" htmlFor="">
                          Hourly Rate
                        </label>
                        <div className="rounded-xl py-4 px-5 flex gap-2 border border-black">
                          <input
                            type="number"
                            className="outline-none w-full text-base font-medium text-black placeholder-black"
                            name="hourlyRate"
                            defaultValue={userDetails.data.data.hourlyRate}
                            ref={hourlyRateInputElement}
                            step=".01"
                            onWheel={(e) => e.target.blur()}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-base text-gray-500" htmlFor="">
                          Overtime Rate
                        </label>
                        <div className="rounded-xl py-4 px-5 flex gap-2 border border-black">
                          <input
                            type="number"
                            className="outline-none w-full text-base font-medium text-black placeholder-black"
                            name="overtimeRate"
                            defaultValue={userDetails.data.data.overtimeRate}
                            ref={overtimeRateInputElement}
                            step=".01"
                            onWheel={(e) => e.target.blur()}
                          />
                        </div>
                      </div>
                    </>
                  )}
                  <div className="space-y-2">
                    <label className="text-base text-gray-500" htmlFor="">
                      Organization
                    </label>
                    <div className="rounded-xl py-4 px-5 flex gap-2 border border-black">
                      <input
                        type="text"
                        className="outline-none w-full text-base font-medium text-black"
                        name="organization"
                        defaultValue={
                          organizationDetails?.data?.data?.name ||
                          "No Organization"
                        }
                        readOnly
                        ref={organizationInputElement}
                      />
                    </div>
                  </div>
                  <div className="pt-4">
                    <button className="px-3 py-4 w-full text-white font-medium rounded-xl bg-[#59c3c2] mb-4">
                      {isUpdateUserLoading ? "Saving..." : "Save"}
                    </button>
                    
                  </div>
                  {isUpdateUserError && (
                    <div className="text-sm font-medium text-red-600 pt-2">
                      <p>{updateUserError.response.data.error}</p>
                    </div>
                  )}
                </div>
              </form>

              {/* Reset Password Modal */}
              {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white p-6 rounded-lg w-96">
                    <h2 className="text-xl font-semibold mb-4">Reset Password</h2>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">New Password</label>
                        <input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#59c3c2] focus:ring-[#59c3c2]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Confirm Password</label>
                        <input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#59c3c2] focus:ring-[#59c3c2]"
                        />
                      </div>
                      {passwordError && (
                        <p className="text-red-500 text-sm">{passwordError}</p>
                      )}
                      <div className="flex justify-end gap-2 mt-4">
                        <button
                          onClick={() => {
                            setIsModalOpen(false);
                            setPasswordError("");
                            setNewPassword("");
                            setConfirmPassword("");
                          }}
                          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleResetPassword}
                          disabled={isResetPasswordLoading}
                          className="px-4 py-2 text-sm font-medium text-white bg-[#59c3c2] hover:bg-teal-600 rounded-md"
                        >
                          {isResetPasswordLoading ? "Resetting..." : "Reset Password"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
